import {
  ActionIcon, Alert, Badge, Button, Card, Drawer, FileButton, Group, Modal, NumberInput, SegmentedControl, Select, SimpleGrid, Slider,
  Stack, Table, Tabs, Text, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowsHorizontal, IconCamera, IconChartLine, IconRuler, IconSparkles, IconTrash, IconTree, IconUpload,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";

import { http } from "../api/http";
import { useCan } from "../auth/user";
import { useT } from "../i18n/script";
import { AuthImage, useAuthBlobUrl } from "./AuthImage";
import { Chart } from "./Charts";
import { DataTable } from "./DataTable";
import { Empty, fmtDate, fmtNum, StatusBadge } from "./ui";

export interface Photo {
  id: number; taken_at: string; kind: string; bbch_code: number | null; width: number; height: number; note: string | null;
  reg_no: string | null; measurement: { id: number; count: number; mean_mm: number; min_mm: number; max_mm: number } | null;
}
interface Growth { series: { datum: string; mean_mm: number; plodova: number; min_mm: number; max_mm: number; prirast_mm_dan?: number | null }[] }
interface Tree {
  id: number; code: string; status: string; row_no: number | null; position_no: number | null; orchard: string; species: string;
  cultivar: string | null; planting_year: number | null; lon: number; lat: number;
  measurements: Record<string, unknown>[]; photos: Photo[]; growth: Growth;
}
type Line = { x1: number; y1: number; x2: number; y2: number; mm?: number; auto?: boolean };

const imgPath = (id: number, size: "thumb" | "display" = "display") => `/api/v1/photos/${id}/image?size=${size}`;

/** Karton stabla: metrika (3D/UAV), fotografije sa mjerenjem plodova, poređenje i prirast. */
export function TreeCardDrawer({ treeId, onClose }: { treeId: number | null; onClose: () => void }) {
  const { t } = useT();
  return (
    <Drawer opened={treeId != null} onClose={onClose} position="right" size="xl" title={<Group gap={6}><IconTree size={18} />{t("Karton stabla")}</Group>}>
      {treeId != null && <TreeCard treeId={treeId} />}
    </Drawer>
  );
}

export function TreeCard({ treeId }: { treeId: number }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tree", treeId], queryFn: () => http.get<Tree>(`/api/v1/trees/${treeId}`) });
  const [measure, setMeasure] = useState<Photo | null>(null);
  const [cmp, setCmp] = useState<[string | null, string | null]>([null, null]);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("plod");
  const d = q.data;
  const last = d?.measurements[d.measurements.length - 1] as Record<string, number | string | null> | undefined;
  const refresh = () => qc.invalidateQueries({ queryKey: ["tree", treeId] });

  async function upload(files: File[]) {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        await http.form(`/api/v1/trees/${treeId}/photos`, fd);
      }
      notifications.show({ color: "green", message: t(`Učitano fotografija: ${files.length}`) });
      refresh();
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    } finally { setUploading(false); }
  }

  if (q.isLoading) return <Text>{t("Učitavanje...")}</Text>;
  if (!d) return <Alert color="red">{String(q.error)}</Alert>;
  const photoOpts = d.photos.map((p) => ({ value: String(p.id), label: `${fmtDate(p.taken_at)}${p.measurement ? ` · ⌀ ${fmtNum(p.measurement.mean_mm, 1)} mm` : ""}` }));

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>{d.code}</Title>
          <Text size="sm" c="dimmed">{d.orchard} · {d.species}{d.cultivar ? ` ${d.cultivar}` : ""}{d.planting_year ? ` · ${d.planting_year}.` : ""}</Text>
          <Text size="xs" c="dimmed">{t("Red")} {d.row_no ?? "—"} · {t("pozicija")} {d.position_no ?? "—"} · {d.lat.toFixed(6)}, {d.lon.toFixed(6)}</Text>
        </div>
        <StatusBadge status={d.status} />
      </Group>
      <Tabs defaultValue="metrika" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="metrika" leftSection={<IconTree size={14} />}>{t("Metrika")}</Tabs.Tab>
          <Tabs.Tab value="foto" leftSection={<IconCamera size={14} />}>{t("Fotografije")} ({d.photos.length})</Tabs.Tab>
          <Tabs.Tab value="poredjenje" leftSection={<IconArrowsHorizontal size={14} />} disabled={d.photos.length < 2}>{t("Poređenje")}</Tabs.Tab>
          <Tabs.Tab value="prirast" leftSection={<IconChartLine size={14} />}>{t("Prirast ploda")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="metrika" pt="sm">
          {!last ? <Empty text="Stablo još nema mjerenja (3D snimak ili UAV)" /> : (
            <Stack>
              <SimpleGrid cols={{ base: 2, sm: 4 }}>
                {[["Visina", last.height_m, "m"], ["Prečnik krošnje", last.crown_diameter_m, "m"], ["Površina krošnje", last.crown_area_m2, "m²"],
                  ["Zapremina krošnje", last.crown_volume_m3, "m³"]].map(([l, v, u]) => (
                  <Card key={String(l)} withBorder p="xs"><Text size="xs" c="dimmed">{t(String(l))}</Text>
                    <Text fw={700}>{fmtNum(v, 2)} <Text span size="xs" c="dimmed">{String(u)}</Text></Text></Card>
                ))}
              </SimpleGrid>
              {last.metrics && typeof last.metrics === "object" && (
                <Table fz="sm" withRowBorders={false} verticalSpacing={2}>
                  <Table.Tbody>
                    {flatMetrics(last.metrics as Record<string, unknown>).map(([k, v]) => (
                      <Table.Tr key={k}><Table.Td c="dimmed">{t(METRIC_LABELS[k] ?? k)}</Table.Td><Table.Td>{typeof v === "number" ? fmtNum(v, 2) : String(v)}</Table.Td></Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              <Text fw={600} size="sm">{t("Mjerenja kroz vrijeme")}</Text>
              <DataTable rows={d.measurements} search={false} dense columns={[
                { key: "datum", label: "Datum", type: "date" }, { key: "source", label: "Izvor" },
                { key: "height_m", label: "Visina (m)", type: "number" }, { key: "crown_diameter_m", label: "Krošnja (m)", type: "number" },
                { key: "crown_volume_m3", label: "Zapremina (m³)", type: "number" }, { key: "algorithm_version", label: "Algoritam" },
              ]} />
            </Stack>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="foto" pt="sm">
          {can("photos:write") && (
            <Group mb="sm">
              <Select size="xs" w={140} value={kind} onChange={(v) => v && setKind(v)} data={[
                { value: "plod", label: t("Plodovi") }, { value: "stablo", label: t("Stablo") }, { value: "list", label: t("List") },
                { value: "steta", label: t("Šteta") }]} />
              <FileButton multiple accept="image/jpeg,image/png" onChange={upload}>
                {(props) => <Button size="xs" leftSection={<IconUpload size={14} />} loading={uploading} {...props}>{t("Dodaj fotografije")}</Button>}
              </FileButton>
              <Text size="xs" c="dimmed">{t("Datum i GPS se čitaju iz EXIF-a; lokacija se ne prikazuje dalje.")}</Text>
            </Group>
          )}
          {!d.photos.length ? <Empty text="Nema fotografija" /> : (
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
              {[...d.photos].reverse().map((p) => (
                <Card key={p.id} withBorder p={0} style={{ cursor: "pointer" }} onClick={() => setMeasure(p)}>
                  <AuthImage path={imgPath(p.id, "thumb")} alt={p.taken_at} h={130} />
                  <Group justify="space-between" p={6} gap={4}>
                    <Text size="xs">{fmtDate(p.taken_at)}</Text>
                    {p.measurement ? <Badge size="xs" variant="light">⌀ {fmtNum(p.measurement.mean_mm, 1)} mm</Badge>
                      : <Badge size="xs" color="gray" variant="outline">{t(p.kind)}</Badge>}
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="poredjenje" pt="sm">
          <Group grow mb="sm">
            <Select size="xs" label={t("Ranija")} data={photoOpts} value={cmp[0] ?? photoOpts[0]?.value ?? null} onChange={(v) => setCmp([v, cmp[1]])} />
            <Select size="xs" label={t("Kasnija")} data={photoOpts} value={cmp[1] ?? photoOpts[photoOpts.length - 1]?.value ?? null} onChange={(v) => setCmp([cmp[0], v])} />
          </Group>
          <Compare a={Number(cmp[0] ?? photoOpts[0]?.value)} b={Number(cmp[1] ?? photoOpts[photoOpts.length - 1]?.value)} photos={d.photos} />
        </Tabs.Panel>

        <Tabs.Panel value="prirast" pt="sm">
          {!d.growth.series.length ? <Empty text="Nema mjerenja plodova — izmjerite plodove na fotografiji" /> : (
            <Stack>
              <Chart type="line" rows={d.growth.series} x="datum" xLabel={(v) => fmtDate(v)} series={[
                { key: "mean_mm", label: t("Prosječan prečnik (mm)") }, { key: "max_mm", label: t("Najveći (mm)"), color: "#e8590c" }]} />
              <DataTable rows={d.growth.series as unknown as Record<string, unknown>[]} search={false} dense columns={[
                { key: "datum", label: "Datum", type: "date" }, { key: "plodova", label: "Plodova", type: "integer" },
                { key: "mean_mm", label: "Prosjek (mm)", type: "number", decimals: 1 }, { key: "min_mm", label: "Min", type: "number", decimals: 1 },
                { key: "max_mm", label: "Max", type: "number", decimals: 1 }, { key: "prirast_mm_dan", label: "Prirast (mm/dan)", type: "number" },
              ]} />
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
      <MeasureModal photo={measure} onClose={() => setMeasure(null)} onSaved={refresh} canWrite={can("photos:write")} />
    </Stack>
  );
}

const METRIC_LABELS: Record<string, string> = {
  h_p95_m: "Visina p95 (m)", h_p50_m: "Visina medijan (m)", crown_base_m: "Početak krošnje iznad tla (m)", points: "Tačaka u krošnji",
  crown_volume_voxel_m3: "Zapremina — vokseli 0,2 m (m³)", crown_diameter_max_m: "Najveći prečnik krošnje (m)",
  gcc: "Indeks zelenila GCC", rcc: "Indeks crvenila RCC (plodovi)", ground_z_m: "Kota tla (m)", density_pts_m2: "Gustina tačaka (/m²)",
};
const flatMetrics = (m: Record<string, unknown>) => Object.entries({ ...m, ...((m.extra as Record<string, unknown>) ?? {}) })
  .filter(([k]) => k in METRIC_LABELS);

/** Poređenje dvije fotografije istog stabla klizačem (praćenje prirasta). */
function Compare({ a, b, photos }: { a: number; b: number; photos: Photo[] }) {
  const { t } = useT();
  const [pos, setPos] = useState(50);
  const ua = useAuthBlobUrl(a ? imgPath(a) : null);
  const ub = useAuthBlobUrl(b ? imgPath(b) : null);
  const pa = photos.find((p) => p.id === a), pb = photos.find((p) => p.id === b);
  if (!ua || !ub || !pa || !pb) return <Empty text="Izaberite dvije fotografije" />;
  const days = Math.round((new Date(pb.taken_at).getTime() - new Date(pa.taken_at).getTime()) / 86400000);
  const dmm = pa.measurement && pb.measurement ? pb.measurement.mean_mm - pa.measurement.mean_mm : null;
  return (
    <Stack gap="xs">
      <div style={{ position: "relative", width: "100%", aspectRatio: `${pa.width} / ${pa.height}`, overflow: "hidden", borderRadius: 8 }}>
        <img src={ua} alt="ranija" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        <img src={ub} alt="kasnija" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
          clipPath: `inset(0 0 0 ${pos}%)` }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: `${pos}%`, width: 2, background: "#fff", boxShadow: "0 0 4px #000" }} />
        <Badge style={{ position: "absolute", top: 8, left: 8 }}>{fmtDate(pa.taken_at)}</Badge>
        <Badge style={{ position: "absolute", top: 8, right: 8 }} color="orange">{fmtDate(pb.taken_at)}</Badge>
      </div>
      <Slider value={pos} onChange={setPos} label={null} />
      <Text size="sm">
        {t("Razmak")}: {days} {t("dana")}
        {dmm != null && <> · {t("promjena prečnika")}: <b>{dmm >= 0 ? "+" : ""}{fmtNum(dmm, 1)} mm</b> ({fmtNum(days ? dmm / days : 0, 2)} mm/{t("dan")})</>}
      </Text>
    </Stack>
  );
}

/** Mjerenje prečnika plodova: referentna linija poznate dužine + linije preko plodova (ručno ili prijedlog). */
function MeasureModal({ photo, onClose, onSaved, canWrite }: { photo: Photo | null; onClose: () => void; onSaved: () => void; canWrite: boolean }) {
  const { t } = useT();
  const url = useAuthBlobUrl(photo ? imgPath(photo.id) : null);
  const [mode, setMode] = useState<"ref" | "fruit">("ref");
  const [ref, setRef] = useState<Line | null>(null);
  const [refMm, setRefMm] = useState<number>(85.6);
  const [lines, setLines] = useState<Line[]>([]);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const svg = useRef<SVGSVGElement>(null);
  const hist = useQuery({ queryKey: ["photo-meas", photo?.id], enabled: !!photo,
    queryFn: () => http.get<Record<string, unknown>[]>(`/api/v1/photos/${photo!.id}/measurements`) });
  const mmPerPx = ref ? refMm / Math.hypot(ref.x2 - ref.x1, ref.y2 - ref.y1) : null;
  const mm = (l: Line) => (mmPerPx ? Math.hypot(l.x2 - l.x1, l.y2 - l.y1) * mmPerPx : null);
  const stats = useMemo(() => {
    const v = lines.map(mm).filter((x): x is number => x != null);
    return v.length ? { n: v.length, mean: v.reduce((a, b) => a + b, 0) / v.length, min: Math.min(...v), max: Math.max(...v) } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, ref, refMm]);

  function reset() { setRef(null); setLines([]); setStart(null); setMode("ref"); }
  function pt(e: React.MouseEvent) {
    const s = svg.current!;
    const p = s.createSVGPoint();
    p.x = e.clientX; p.y = e.clientY;
    const r = p.matrixTransform(s.getScreenCTM()!.inverse());
    return { x: Math.round(r.x * 10) / 10, y: Math.round(r.y * 10) / 10 };
  }
  function click(e: React.MouseEvent) {
    if (!canWrite) return;
    const p = pt(e);
    if (!start) { setStart(p); return; }
    const l = { x1: start.x, y1: start.y, x2: p.x, y2: p.y };
    setStart(null);
    if (mode === "ref") { setRef(l); setMode("fruit"); } else setLines([...lines, l]);
  }
  async function auto() {
    if (!photo || !ref) return;
    setBusy(true);
    try {
      const r = await http.post<{ suggestions: Line[] }>(`/api/v1/photos/${photo.id}/auto-detect`, { ref: { ...ref, length_mm: refMm } });
      setLines([...lines, ...r.suggestions.map((s) => ({ ...s, auto: true }))]);
      notifications.show({ message: t(`Prijedlog: ${r.suggestions.length} plodova — provjerite i uklonite pogrešne (klik na liniju).`) });
    } catch (e) { notifications.show({ color: "red", message: String(e) }); } finally { setBusy(false); }
  }
  async function save() {
    if (!photo || !ref || !lines.length) return;
    setBusy(true);
    try {
      await http.post(`/api/v1/photos/${photo.id}/measurements`, {
        ref: { ...ref, length_mm: refMm }, fruits: lines.map(({ x1, y1, x2, y2 }) => ({ x1, y1, x2, y2 })),
        method: lines.some((l) => l.auto) ? "poluautomatski" : "rucno" });
      notifications.show({ color: "green", message: t("Mjerenje je sačuvano") });
      reset(); hist.refetch(); onSaved();
    } catch (e) { notifications.show({ color: "red", message: String(e) }); } finally { setBusy(false); }
  }
  async function del(id: number) {
    await http.del(`/api/v1/measurements/${id}`);
    hist.refetch(); onSaved();
  }

  return (
    <Modal opened={!!photo} onClose={() => { reset(); onClose(); }} size="90%" title={photo ? `${t("Fotografija")} ${fmtDate(photo.taken_at)} · ${photo.reg_no ?? ""}` : ""}>
      {photo && (
        <Group align="flex-start" wrap="nowrap" gap="md">
          <div style={{ flex: 1, position: "relative" }}>
            {url ? (
              <svg ref={svg} viewBox={`0 0 ${photo.width} ${photo.height}`} style={{ width: "100%", display: "block", cursor: canWrite ? "crosshair" : "default", borderRadius: 8 }}
                onClick={click} onMouseMove={(e) => start && setHover(pt(e))}>
                <image href={url} x={0} y={0} width={photo.width} height={photo.height} />
                {!ref && !lines.length && ((hist.data?.[0]?.items as Line[] | undefined) ?? []).map((l, i) => (
                  <g key={`s${i}`} opacity={0.85}>
                    <line x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#51cf66" strokeWidth={3} strokeDasharray="10 6" />
                    <text x={(l.x1 + l.x2) / 2} y={Math.min(l.y1, l.y2) - 8} textAnchor="middle" fill="#fff" fontSize={22} fontWeight={700}
                      stroke="#000" strokeWidth={0.8}>{fmtNum(l.mm, 1)}</text>
                  </g>
                ))}
                {ref && <line {...ref} stroke="#00e5ff" strokeWidth={4} />}
                {ref && <text x={ref.x2 + 8} y={ref.y2} fill="#00e5ff" fontSize={26} fontWeight={700} stroke="#000" strokeWidth={0.6}>{refMm} mm</text>}
                {lines.map((l, i) => (
                  <g key={i} onClick={(e) => { e.stopPropagation(); setLines(lines.filter((_, j) => j !== i)); }} style={{ cursor: "pointer" }}>
                    <line {...l} stroke={l.auto ? "#ffd43b" : "#ff6b6b"} strokeWidth={4} />
                    <circle cx={(l.x1 + l.x2) / 2} cy={(l.y1 + l.y2) / 2} r={Math.hypot(l.x2 - l.x1, l.y2 - l.y1) / 2} fill="none"
                      stroke={l.auto ? "#ffd43b" : "#ff6b6b"} strokeDasharray="8 6" strokeWidth={2} />
                    {mm(l) != null && <text x={(l.x1 + l.x2) / 2} y={Math.min(l.y1, l.y2) - 8} textAnchor="middle" fill="#fff" fontSize={24}
                      fontWeight={700} stroke="#000" strokeWidth={0.8}>{fmtNum(mm(l), 1)}</text>}
                  </g>
                ))}
                {start && hover && <line x1={start.x} y1={start.y} x2={hover.x} y2={hover.y} stroke={mode === "ref" ? "#00e5ff" : "#ff6b6b"} strokeWidth={3} strokeDasharray="6 4" />}
              </svg>
            ) : <Text>{t("Učitavanje...")}</Text>}
          </div>
          <Stack w={300} gap="sm">
            {canWrite && (
              <>
                <SegmentedControl size="xs" value={mode} onChange={(v) => { setMode(v as "ref" | "fruit"); setStart(null); }} data={[
                  { value: "ref", label: t("1. Referenca") }, { value: "fruit", label: t("2. Plodovi"), disabled: !ref }]} />
                <NumberInput size="xs" label={t("Dužina reference (mm)")} description={t("Kartica 85,6 mm ili lenjir")} value={refMm}
                  onChange={(v) => setRefMm(Number(v) || 85.6)} decimalScale={1} min={1} />
                <Text size="xs" c="dimmed">
                  {mode === "ref" ? t("Kliknite na početak i kraj referentnog predmeta.") : t("Kliknite na dvije suprotne ivice ploda (prečnik). Klik na liniju je uklanja.")}
                </Text>
                <Group gap={6}>
                  <Tooltip label={t("Prepoznavanje kružnih plodova (Hough) — prijedlog za provjeru")}>
                    <Button size="xs" variant="light" leftSection={<IconSparkles size={14} />} disabled={!ref} loading={busy} onClick={auto}>{t("Predloži plodove")}</Button>
                  </Tooltip>
                  <Button size="xs" variant="subtle" color="gray" onClick={reset}>{t("Poništi")}</Button>
                </Group>
                {stats && (
                  <Card withBorder p="xs">
                    <Text size="sm">{t("Plodova")}: <b>{stats.n}</b> · ⌀ <b>{fmtNum(stats.mean, 1)} mm</b></Text>
                    <Text size="xs" c="dimmed">{t("raspon")} {fmtNum(stats.min, 1)}–{fmtNum(stats.max, 1)} mm</Text>
                  </Card>
                )}
                <Button leftSection={<IconRuler size={16} />} disabled={!ref || !lines.length} loading={busy} onClick={save}>{t("Sačuvaj mjerenje")}</Button>
              </>
            )}
            <Text fw={600} size="sm">{t("Sačuvana mjerenja")}</Text>
            {(hist.data ?? []).length === 0 ? <Text size="xs" c="dimmed">—</Text> : (hist.data ?? []).map((m) => (
              <Group key={String(m.id)} justify="space-between" wrap="nowrap">
                <Text size="xs">{fmtDate(m.measured_on)} · {String(m.count)} × ⌀ {fmtNum(m.mean_mm, 1)} mm · {String(m.method)} · {String(m.created_by)}</Text>
                {canWrite && <ActionIcon size="sm" color="red" variant="subtle" onClick={() => del(Number(m.id))}><IconTrash size={14} /></ActionIcon>}
              </Group>
            ))}
          </Stack>
        </Group>
      )}
    </Modal>
  );
}
